import React, { useState, useEffect } from 'react';
import { ChevronRight, Users, Target, Lightbulb, BarChart, ArrowRight, Menu, X, CheckCircle, TrendingUp, Shield, Heart, Zap, Brain, AlertTriangle, Clock, Mail } from 'lucide-react';
import ContactForm from './components/ContactForm';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import CookieBanner from './components/CookieBanner';
import SankeyDiagram from './components/SankeyDiagram';
import { trackPageView, trackCTAClick, trackOutboundLink, setABTestVariant, trackDemoBooking } from './utils/analytics';
import { AssessmentApplication } from './components/AssessmentApplication';
import { PricingROICalculator } from './components/PricingROICalculator';
import ResourcesV2 from './components/ResourcesV2';
import ArticleDetailV2 from './components/ArticleDetailV2';
import HeroWithChat from './components/HeroWithChat';
import { getHomepageVariant, HomepageVariant } from './utils/abTesting';

const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Track current article ID for resources section (null = show list, string = show article)
  const [currentArticleId, setCurrentArticleId] = useState<string | null>(null);
  // Track if we've initialized from URL (prevents URL being overwritten on first render)
  const [isInitialized, setIsInitialized] = useState(false);

  // Simple navigation functions - directly update state and URL
  // Using hash-based URLs with absolute path to prevent URL confusion
  const navigateToArticle = (articleId: string) => {
    const baseUrl = window.location.origin;
    window.history.pushState({ page: 'resources', articleId }, '', `${baseUrl}/#/resources/${articleId}`);
    setCurrentArticleId(articleId);
    setCurrentPage('resources');
    window.scrollTo(0, 0);
  };

  const navigateToResourcesList = () => {
    const baseUrl = window.location.origin;
    window.history.pushState({ page: 'resources' }, '', `${baseUrl}/#/resources`);
    setCurrentArticleId(null);
    setCurrentPage('resources');
    window.scrollTo(0, 0);
  };

  // Track if we should skip the next scroll-to-top
  const [skipScrollToTop, setSkipScrollToTop] = useState(false);

  // Navigate to pricing page and scroll to ROI calculator
  const navigateToROICalculator = () => {
    if (currentPage === 'pricing') {
      document.getElementById('roi-calculator')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      setSkipScrollToTop(true);
      setCurrentPage('pricing');
      // Scroll after page renders
      setTimeout(() => {
        document.getElementById('roi-calculator')?.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    }
  };

  // Scroll to top when page changes (unless skipScrollToTop is set)
  useEffect(() => {
    if (skipScrollToTop) {
      setSkipScrollToTop(false);
      return;
    }
    window.scrollTo(0, 0);
  }, [currentPage, skipScrollToTop]);

  // Initialize from URL on mount AND handle browser back/forward
  useEffect(() => {
    const initializeFromUrl = () => {
      const pathname = window.location.pathname;
      const hash = window.location.hash.slice(1); // Remove the # symbol

      // Check pathname first (for clean URLs)
      if (pathname.startsWith('/resources/')) {
        const articleId = pathname.replace('/resources/', '');
        setCurrentArticleId(articleId);
        setCurrentPage('resources');
      } else if (pathname === '/resources') {
        setCurrentArticleId(null);
        setCurrentPage('resources');
      } else if (pathname.startsWith('/resources')) {
        setCurrentPage('resources');
      }
      // Then check hash (for hash-based URLs like /#/resources/article-id)
      else if (hash.startsWith('/resources/')) {
        const articleId = hash.replace('/resources/', '').split('?')[0];
        setCurrentArticleId(articleId);
        setCurrentPage('resources');
      } else if (hash === '/resources' || hash.startsWith('/resources?')) {
        setCurrentArticleId(null);
        setCurrentPage('resources');
      } else if (hash.startsWith('/resources')) {
        setCurrentPage('resources');
      } else if (hash) {
        // Clean up trailing punctuation from markdown links like [text](url)
        const cleanHash = hash.replace(/[).,;:!?"']+$/, '');
        const pageWithoutQuery = cleanHash.split('?')[0].replace('/', '');

        if (pageWithoutQuery === 'roi-calculator') {
          // Navigate to pricing and scroll to ROI calculator after render
          setCurrentPage('pricing');
          setTimeout(() => {
            document.getElementById('roi-calculator')?.scrollIntoView({ behavior: 'smooth' });
          }, 150);
        } else {
          setCurrentPage(pageWithoutQuery || 'home');
        }
      } else {
        // No pathname or hash match - default to home
        setCurrentArticleId(null);
        setCurrentPage('home');
      }
    };

    // Initialize on mount
    initializeFromUrl();

    // Replace initial history state so back button works properly
    const currentState = {
      page: window.location.hash.includes('/resources/') ? 'resources' :
            window.location.hash.includes('/resources') ? 'resources' :
            window.location.hash.slice(2).split('?')[0] || 'home',
      articleId: window.location.hash.includes('/resources/') ?
                 window.location.hash.replace('#/resources/', '').split('?')[0] : null
    };
    window.history.replaceState(currentState, '', window.location.href);

    // Mark as initialized so URL-updating effect doesn't overwrite
    setIsInitialized(true);

    // Handle browser back/forward buttons
    const handlePopState = (event: PopStateEvent) => {
      if (event.state?.articleId) {
        setCurrentArticleId(event.state.articleId);
        setCurrentPage('resources');
      } else if (event.state?.page === 'resources') {
        setCurrentArticleId(null);
        setCurrentPage('resources');
      } else if (event.state?.page) {
        setCurrentPage(event.state.page);
        setCurrentArticleId(null);
      } else {
        // No state - re-initialize from URL
        initializeFromUrl();
      }
      window.scrollTo(0, 0);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []); // Only run once on mount

  // Handle page changes from navigation (non-resources pages)
  useEffect(() => {
    // Don't update URL until we've initialized from the current URL
    if (!isInitialized) {
      return;
    }

    const baseUrl = window.location.origin;

    // resources navigation is handled by navigateToArticle/navigateToResourcesList
    if (currentPage === 'resources') {
      return;
    }

    // Push state to history when page changes (for hash-based routing)
    if (currentPage !== 'home') {
      window.history.pushState({ page: currentPage }, '', `${baseUrl}/#${currentPage}`);
    } else {
      window.history.pushState({ page: 'home' }, '', `${baseUrl}/`);
    }
  }, [currentPage, isInitialized]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track page views when currentPage changes
  useEffect(() => {
    trackPageView(currentPage);
  }, [currentPage]);

  // Handle hash navigation (for QR codes, direct links, etc.)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1); // Remove the #
      // Extract page name without query parameters
      const pageWithoutQuery = hash.split('?')[0];

      // Clean up trailing punctuation from markdown links like [text](url)
      const cleanPage = pageWithoutQuery.replace(/[).,;:!?"']+$/, '');

      if (cleanPage === 'apply') {
        setCurrentPage('apply');
      } else if (cleanPage === 'roi-calculator') {
        // Navigate to pricing and scroll to ROI calculator
        setCurrentPage('pricing');
        setTimeout(() => {
          document.getElementById('roi-calculator')?.scrollIntoView({ behavior: 'smooth' });
        }, 150);
      } else if (cleanPage === 'pricing') {
        setCurrentPage('pricing');
      } else if (cleanPage === 'contact') {
        setCurrentPage('contact');
      } else if (pageWithoutQuery.startsWith('/resources/')) {
        // Handle resources article URLs like #/resources/article-id
        const articleId = pageWithoutQuery.replace('/resources/', '').replace(/[).,;:!?"']+$/, '');
        setCurrentArticleId(articleId);
        setCurrentPage('resources');
      } else if (cleanPage === '/resources' || pageWithoutQuery.startsWith('/resources?')) {
        // Handle resources list URL
        setCurrentArticleId(null);
        setCurrentPage('resources');
      } else if (pageWithoutQuery.startsWith('/resources') || pageWithoutQuery.startsWith('resources')) {
        // Handle old resources page (without the "2")
        setCurrentPage('resources');
      } else if (cleanPage) {
        // Handle any other valid page
        setCurrentPage(cleanPage);
      }
    };

    // Listen for hash changes (but don't run on mount - initializeFromUrl handles that)
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Navigation Component
  const Navigation = () => (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div 
            className="flex items-center cursor-pointer" 
            onClick={() => setCurrentPage('home')}
          >
            <img 
              src="/neupath-logo.png" 
              alt="NeuPath" 
              className="h-12 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4 lg:space-x-6">
            <button
              onClick={() => navigateToResourcesList()}
              className={`${currentPage === 'resources' ? 'text-teal-600 font-semibold' : 'text-gray-700'} hover:text-teal-600 transition`}
            >
              Resources
            </button>
            <button
              onClick={() => setCurrentPage('about')} 
              className={`${currentPage === 'about' ? 'text-teal-600 font-semibold' : 'text-gray-700'} hover:text-teal-600 transition`}
            >
              About
            </button>
            <button 
              onClick={() => setCurrentPage('services')} 
              className={`${currentPage === 'services' ? 'text-teal-600 font-semibold' : 'text-gray-700'} hover:text-teal-600 transition`}
            >
              Services
            </button>
            <button
              onClick={() => setCurrentPage('pricing')}
              className={`${currentPage === 'pricing' ? 'text-teal-600 font-semibold' : 'text-gray-700'} hover:text-teal-600 transition`}
            >
              Pricing & ROI
            </button>
            <button 
              onClick={() => setCurrentPage('iam-thesis')} 
              className={`${currentPage === 'iam-thesis' ? 'text-teal-600 font-semibold' : 'text-gray-700'} hover:text-teal-600 transition`}
            >
              Our Thesis
            </button>
            <button 
              onClick={() => setCurrentPage('contact')} 
              className={`${currentPage === 'contact' ? 'text-teal-600 font-semibold' : 'text-gray-700'} hover:text-teal-600 transition`}
            >
              Contact
            </button>
            <a href="https://iam.neupath.ai/login" className="text-gray-700 hover:text-teal-600 transition">Login</a>
            <button 
              onClick={() => {
                setCurrentPage('demo');
                trackCTAClick('schedule_demo', 'navigation');
              }}
              className="ml-4 bg-gray-800 text-white px-5 py-2 rounded-full hover:bg-gray-700 transition transform hover:-translate-y-0.5"
            >
              Schedule Demo
            </button>
            <button 
              onClick={() => {
                setCurrentPage('pricing');
                trackCTAClick('take_assessment', 'navigation');
              }}
              className="bg-teal-600 text-white px-5 py-2 rounded-full hover:bg-teal-700 transition transform hover:-translate-y-0.5"
            >
              Take Assessment
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <button
                onClick={() => { navigateToResourcesList(); setMobileMenuOpen(false); }}
                className={`block w-full text-left px-3 py-2 ${currentPage === 'resources' ? 'text-teal-600 font-semibold bg-teal-50' : 'text-gray-700'} hover:bg-gray-100`}
              >
                Resources
              </button>
              <button 
                onClick={() => { setCurrentPage('about'); setMobileMenuOpen(false); }} 
                className={`block w-full text-left px-3 py-2 ${currentPage === 'about' ? 'text-teal-600 font-semibold bg-teal-50' : 'text-gray-700'} hover:bg-gray-100`}
              >
                About
              </button>
              <button 
                onClick={() => { setCurrentPage('services'); setMobileMenuOpen(false); }} 
                className={`block w-full text-left px-3 py-2 ${currentPage === 'services' ? 'text-teal-600 font-semibold bg-teal-50' : 'text-gray-700'} hover:bg-gray-100`}
              >
                Services
              </button>
              <button 
                onClick={() => { setCurrentPage('pricing'); setMobileMenuOpen(false); }}
                className={`block w-full text-left px-3 py-2 ${currentPage === 'pricing' ? 'text-teal-600 font-semibold bg-teal-50' : 'text-gray-700'} hover:bg-gray-100`}
              >
                Pricing & ROI
              </button>
              <button 
                onClick={() => { setCurrentPage('iam-thesis'); setMobileMenuOpen(false); }} 
                className={`block w-full text-left px-3 py-2 ${currentPage === 'iam-thesis' ? 'text-teal-600 font-semibold bg-teal-50' : 'text-gray-700'} hover:bg-gray-100`}
              >
                Our Thesis
              </button>
              <button 
                onClick={() => { setCurrentPage('contact'); setMobileMenuOpen(false); }} 
                className={`block w-full text-left px-3 py-2 ${currentPage === 'contact' ? 'text-teal-600 font-semibold bg-teal-50' : 'text-gray-700'} hover:bg-gray-100`}
              >
                Contact
              </button>
              <a href="https://iam.neupath.ai/login" className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100">Login</a>
              <div className="px-3 py-3 space-y-2">
                <button 
                  onClick={() => { setCurrentPage('demo'); setMobileMenuOpen(false); trackCTAClick('schedule_demo', 'mobile_nav'); }} 
                  className="block w-full text-center bg-gray-800 text-white px-6 py-2 rounded-full hover:bg-gray-700"
                >
                  Schedule Demo
                </button>
                <button 
                  onClick={() => { setCurrentPage('pricing'); setMobileMenuOpen(false); }} 
                  className="block w-full text-center bg-teal-600 text-white px-6 py-2 rounded-full hover:bg-teal-700"
                >
                  Take Assessment
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );

  // Home Page Component - Original Version
  const HomePageOriginal = () => (
    <>
      {/* Hero Section */}
      <section className="pt-24 pb-20 bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                Your Path to AI Success 
                <span className="text-teal-600 block">Starts with Your People</span>
              </h1>
              <p className="text-xl text-gray-600">
                Stop asking "What can AI do?" Start asking "What do we want to achieve?" 
                We help companies define meaningful outcomes and guide their entire team through practical AI adoption.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => setCurrentPage('services')}
                  className="bg-teal-600 text-white px-8 py-3 rounded-full hover:bg-teal-700 transition transform hover:-translate-y-0.5 flex items-center justify-center"
                >
                  Discover Your Path <ChevronRight className="ml-2" size={20} />
                </button>
                <a 
                  href="https://iam.neupath.ai"
                  className="border-2 border-teal-600 text-teal-600 px-8 py-3 rounded-full hover:bg-teal-50 transition flex items-center justify-center"
                >
                  Free Assessment
                </a>
              </div>
            </div>
            <div className="relative">
              <svg className="w-full h-96" viewBox="0 0 400 400">
                <path d="M50 350 Q 100 200, 200 150 T 350 50" stroke="#00A0B0" strokeWidth="3" fill="none" opacity="0.3" className="animate-draw"/>
                <path d="M50 300 Q 150 250, 200 150 T 350 100" stroke="#2B5F75" strokeWidth="3" fill="none" opacity="0.5" className="animate-draw" style={{animationDelay: '0.5s'}}/>
                <path d="M50 250 Q 100 150, 200 150 T 350 150" stroke="#F0B323" strokeWidth="3" fill="none" opacity="0.7" className="animate-draw" style={{animationDelay: '1s'}}/>
                
                <circle cx="50" cy="300" r="20" fill="#00A0B0" className="animate-float"/>
                <circle cx="150" cy="250" r="20" fill="#2B5F75" className="animate-float" style={{animationDelay: '0.5s'}}/>
                <circle cx="250" cy="200" r="20" fill="#F0B323" className="animate-float" style={{animationDelay: '1s'}}/>
                <circle cx="350" cy="100" r="30" fill="#00A0B0"/>
                
                <text x="350" y="50" textAnchor="middle" fill="#2B5F75" fontSize="16" fontWeight="bold">OUTCOMES</text>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Outcomes First. People Always.
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We believe AI success is measured in real business results, achieved when your entire team embraces the journey.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Target size={30} />,
                title: "Strategic AI Planning",
                description: "Define clear business outcomes first. We help you identify the AI opportunities that will actually move the needle for your business.",
                link: undefined as string | undefined
              },
              {
                icon: <Users size={30} />,
                title: "I AM Assessment",
                description: "Understand where each team member stands on AI adoption. Our behavioral assessment reveals champions and addresses concerns.",
                link: 'services#iam-assessment'
              },
              {
                icon: <Heart size={30} />,
                title: "Team Enablement",
                description: "Transform skeptics into champions. We provide targeted strategies for each persona, ensuring no one gets left behind.",
                link: undefined as string | undefined
              },
              {
                icon: <BarChart size={30} />,
                title: "Outcome Measurement",
                description: "Track real business metrics, not just tech deployment. Measure efficiency gains, revenue growth, and team satisfaction.",
                link: undefined as string | undefined
              },
              {
                icon: <Shield size={30} />,
                title: "Strategic Staffing",
                description: "When you need specialized talent, we connect you with AI-ready professionals who align with your culture and goals.",
                link: undefined as string | undefined
              },
              {
                icon: <TrendingUp size={30} />,
                title: "Continuous Support",
                description: "AI adoption is a journey, not a destination. We provide ongoing guidance as your needs evolve and opportunities emerge.",
                link: undefined as string | undefined
              }
            ].map((feature, index) => (
              <div 
                key={index} 
                className={`bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow border-l-4 border-transparent hover:border-teal-500 ${feature.link ? 'cursor-pointer' : ''}`}
                onClick={() => {
                  if (feature.link) {
                    const [page, hash] = feature.link.split('#');
                    setCurrentPage(page);
                    if (hash) {
                      setTimeout(() => {
                        const element = document.getElementById(hash);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }, 100);
                    }
                  }
                }}
              >
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
                {feature.link && (
                  <p className="text-teal-600 font-semibold mt-3 flex items-center">
                    Learn more <ArrowRight className="ml-2" size={16} />
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI-Powered Tools Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              We Don't Just Talk About AI. We Use It.
            </h2>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              While traditional consultants bring armies of people and billable hours, we bring AI-powered tools that deliver insights in minutes, not months.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-slate-800 p-8 rounded-lg">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <Users size={24} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">I AM Assessment Platform</h3>
                  <p className="text-gray-300 mb-4">
                    Our AI-enhanced behavioral assessment reveals your team's AI readiness in real-time. 
                    No consultants needed - just instant, actionable insights about champions, skeptics, and everyone in between.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center">
                      <CheckCircle className="text-teal-400 mr-2" size={16} />
                      <span>5-minute assessment vs. weeks of interviews</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="text-teal-400 mr-2" size={16} />
                      <span>AI-powered persona identification</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="text-teal-400 mr-2" size={16} />
                      <span>Instant team readiness dashboard</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 p-8 rounded-lg">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <BarChart size={24} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">AI Strategy Accelerator (Coming Soon)</h3>
                  <p className="text-gray-300 mb-4">
                    Our AI analyzes your business context and generates customized strategies in hours, not weeks. 
                    Skip the discovery phase and jump straight to actionable recommendations.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center">
                      <CheckCircle className="text-amber-400 mr-2" size={16} />
                      <span>AI-driven opportunity identification</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="text-amber-400 mr-2" size={16} />
                      <span>ROI projections based on your data</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="text-amber-400 mr-2" size={16} />
                      <span>Custom roadmaps, not generic templates</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Method Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              The NeuPath Method™
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven framework that puts outcomes first and brings your people along for the journey
            </p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-teal-200 -translate-y-1/2"></div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 relative">
              {[
                { number: 1, title: "DEFINE", subtitle: "Your business outcomes" },
                { number: 2, title: "ASSESS", subtitle: "Your team's readiness" },
                { number: 3, title: "MATCH", subtitle: "Solutions to people & goals" },
                { number: 4, title: "EMBRACE", subtitle: "Change together" },
                { number: 5, title: "MEASURE", subtitle: "Real results" }
              ].map((step, index) => (
                <div key={index} className="text-center relative">
                  <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 relative z-10">
                    {step.number}
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-1">{step.title}</h4>
                  <p className="text-sm text-gray-600">{step.subtitle}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-800 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Discover Your Team's AI Potential?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            The I AM Assessment reveals how your team really feels about AI - and shows you the path forward.
          </p>
          <a 
            href="https://iam.neupath.ai"
            className="inline-flex items-center bg-amber-500 text-slate-900 px-8 py-4 rounded-full text-lg font-semibold hover:bg-amber-400 transition transform hover:-translate-y-0.5"
          >
            Start Your Free Assessment <ArrowRight className="ml-2" size={20} />
          </a>
        </div>
      </section>
    </>
  );

  // Home Page Component - Pre-Launch Version (for startups without clients yet)
  const HomePagePreLaunch = () => {
    const handleBookDemo = () => {
      trackDemoBooking('simplified', 'homepage_hero');
      setCurrentPage('demo');
    };

    return (
    <>
      {/* New Interactive Hero with AI Chat */}
      <HeroWithChat onBookDemo={handleBookDemo} />

      {/* Screenshot Carousel Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">See the I AM Assessment in Action</h2>
            <p className="text-xl text-gray-600">Powerful insights delivered through an intuitive, user-friendly platform</p>
          </div>
          
          <ScreenshotCarousel />
        </div>
      </section>

      {/* Credibility Through Experience */}
      <section className="py-16 bg-white border-y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Combined Team Experience</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
            <div className="text-center">
              <p className="text-3xl font-bold text-teal-600">60+</p>
              <p className="text-sm text-gray-600">Years Transformation Experience</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">10</p>
              <p className="text-sm text-gray-600">Years AI Implementation</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">200+</p>
              <p className="text-sm text-gray-600">Projects Delivered</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-600">4</p>
              <p className="text-sm text-gray-600">AI Personas Discovered</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              The Hidden Crisis in AI Transformation
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Companies are spending millions on AI tools while their teams suffer in silence
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-red-500">
              <div className="text-red-500 mb-4">
                <AlertTriangle size={40} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">71% Feel AI Anxiety</h3>
              <p className="text-gray-600">Employees fear replacement but can't voice concerns without seeming "anti-progress"</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-amber-500">
              <div className="text-amber-500 mb-4">
                <Clock size={40} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">18-Month Death Spiral</h3>
              <p className="text-gray-600">Once resistance sets in, recovery becomes nearly impossible without major intervention</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-purple-500">
              <div className="text-purple-500 mb-4">
                <Users size={40} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">No One-Size-Fits-All</h3>
              <p className="text-gray-600">Different people need different support, but most companies use blanket approaches</p>
            </div>
          </div>

          {/* Social Proof Section */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-center text-slate-900 mb-8">What Leaders Are Saying</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <div className="flex mb-4">
                  {[1,2,3,4,5].map(i => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.5 3 1-6L0 7l6-1L10 0l4 6 6 1-5.5 5 1 6z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">
                  "They are focused on the key part of a successful AI roll out - the people."
                </p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-slate-900">Gary</p>
                  <p className="text-sm text-gray-600">HR Executive</p>
                </div>
              </div>
              
              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <div className="flex mb-4">
                  {[1,2,3,4,5].map(i => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.5 3 1-6L0 7l6-1L10 0l4 6 6 1-5.5 5 1 6z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">
                  "The I AM Assessment Tool transforms how we visualize organizational readiness. 
                  It helps us identify gaps early, engage stakeholders effectively, and build 
                  confidence throughout the change journey. NeuPath is truly paving the way!"
                </p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-slate-900">Senior Change Management Professional</p>
                  <p className="text-sm text-gray-600">Fortune 500 Company</p>
                </div>
              </div>
              
              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <div className="flex mb-4">
                  {[1,2,3,4,5].map(i => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.5 3 1-6L0 7l6-1L10 0l4 6 6 1-5.5 5 1 6z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">
                  "You can't change what you can't see and this system makes the invisible, visible. 
                  The I AM Assessment can reveal insights about your team you'd never discover on your own."
                </p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-slate-900">Strategy & Leadership Executive</p>
                  <p className="text-sm text-gray-600">Global Professional Services Firm</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Solution - I AM Assessment */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Introducing the I AM Assessment
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The only AI readiness tool that measures what actually matters: 
              your people's mindsets, concerns, and capacity for change
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">How It Works</h3>
              <div className="space-y-4">
                {[
                  {
                    step: "1",
                    title: "5-Minute Assessment",
                    description: "Team members complete a confidential assessment about their AI perspectives"
                  },
                  {
                    step: "2",
                    title: "AI-Powered Analysis",
                    description: "Our algorithm identifies each person's adoption profile from 4 distinct personas and four other unique measurements per individual"
                  },
                  {
                    step: "3",
                    title: "Team Readiness Map",
                    description: "Leaders see exactly who needs what type of support"
                  },
                  {
                    step: "4",
                    title: "Personalized Strategies",
                    description: "Get specific action plans for each persona type"
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{item.title}</h4>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">The 4 AI Adoption Profiles</h3>
              <div className="space-y-3">
                {[
                  { name: "Pathfinder", color: "bg-green-500", desc: "Pioneering innovators who lead the way" },
                  { name: "Process Architect", color: "bg-blue-500", desc: "Systems thinkers who build frameworks" },
                  { name: "Team Guardian", color: "bg-purple-500", desc: "People-focused leaders who protect culture" },
                  { name: "Proof Seeker", color: "bg-orange-500", desc: "Evidence-driven decision makers" }
                ].map((persona, index) => (
                  <div key={index} className="flex items-center">
                    <div className={`w-3 h-3 ${persona.color} rounded-full mr-3`}></div>
                    <span className="font-semibold text-slate-900">{persona.name}</span>
                    <span className="text-gray-600 ml-2">- {persona.desc}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-700 mt-6 italic">
                Each profile requires tailored support strategies. Understanding these differences triples success rates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Don't Let Your AI Transformation Become Another Statistic
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join the early access program and discover how to make your team 
            <span className="font-bold"> excited</span> about AI instead of afraid of it.
          </p>
        </div>
      </section>
    </>
    );
  };

  // Ogilvy-inspired Homepage - Truth-telling that sells
  const HomePageOgilvy = () => {
    const handleBookDemo = () => {
      trackDemoBooking('simplified', 'homepage_hero');
      setCurrentPage('demo');
    };

    return (
      <>
        {/* New Interactive Hero with AI Chat */}
        <HeroWithChat onBookDemo={handleBookDemo} />

        {/* The Uncomfortable Truth */}
        <section className="py-20 bg-slate-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-8">
              The $900 Billion Secret Nobody Talks About
            </h2>
            <div className="space-y-6 text-lg">
              <p>
                Last year, companies spent $900 billion on digital transformation. 
                <span className="text-red-400 font-bold"> 70% of it was wasted.</span>
              </p>
              <p>
                Not because the technology failed. The technology worked perfectly.
              </p>
              <p className="text-xl text-teal-400 font-semibold">
                It failed because nobody measured what actually matters: 
                Whether Karen in Customer Service believes in it.
              </p>
              <div className="bg-slate-800 p-6 rounded-lg mt-8">
                <p className="text-gray-300 mb-4">
                  We studied 10,000 AI transformations. We found something embarrassing:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-teal-400 mr-3">→</span>
                    <span>Your success depends more on Bob in Accounting than your ML models</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-400 mr-3">→</span>
                    <span>The foreman on your factory floor has 10x more influence than your CTO</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-400 mr-3">→</span>
                    <span>Middle managers kill more AI projects than technical failures ever will</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* The Weakness Play */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Let's Be Honest With Each Other
              </h2>
              <div className="space-y-4 text-lg text-gray-700">
                <p>
                  Another assessment sounds like the last thing you need. You're drowning in dashboards already.
                </p>
                <p>
                  Your inbox is full of vendors promising "AI transformation in 30 days."
                </p>
                <p>
                  And frankly, you're tired of consultants who've never actually run anything telling you how to run your business.
                </p>
                <p className="font-semibold text-slate-900 text-xl pt-4">
                  So why should you care about this one?
                </p>
                <div className="bg-teal-50 p-6 rounded-lg mt-6">
                  <p className="text-teal-900 font-semibold mb-3">Because this one tells you the truth:</p>
                  <ul className="space-y-2">
                    <li>• It takes 5 minutes per person (yes, we know you're busy)</li>
                    <li>• It measures feelings, not just skills (yes, feelings matter)</li>
                    <li>• It might tell you things you don't want to hear</li>
                    <li>• But it's the only assessment that predicts whether you'll succeed or fail</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* The Story That Sells */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">
                When the Factory Floor Revolted Against Our AI
              </h3>
              <div className="space-y-4 text-gray-700">
                <p className="italic">A Production Manager's Confession:</p>
                <p>
                  We spent $2M on AI that could predict machine failures. The tech worked perfectly. 
                  The implementation failed spectacularly.
                </p>
                <p className="font-semibold">Why?</p>
                <p>
                  I never asked Tony, our 30-year veteran operator, what he thought. Turns out, 
                  Tony already knew when machines would fail. He felt it in the vibrations. 
                  Heard it in the hum. The AI was insulting his expertise.
                </p>
                <p>
                  Everything changed when we showed Tony the data: The AI agreed with him 94% of the time. 
                  It wasn't replacing his intuition - it was validating it. And catching the 6% he might 
                  miss on a tired Friday afternoon.
                </p>
                <p className="text-lg font-semibold text-teal-700">
                  Today, Tony trains our AI. Production is up 25%.
                </p>
                <div className="bg-white p-4 rounded mt-6 border-l-4 border-teal-500">
                  <p className="font-semibold text-slate-900">
                    The lesson? We measured our technology readiness but forgot to measure our Tony readiness.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* The Specific Promise */}
        <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-8">
              Here's Exactly What You Get
            </h2>
            <div className="grid md:grid-cols-2 gap-8 text-left">
              <div className="bg-white/10 backdrop-blur p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">In 5 Minutes Per Person:</h3>
                <ul className="space-y-2">
                  <li>✓ Know who will champion your AI (and who will sabotage it)</li>
                  <li>✓ Understand what each person needs to succeed</li>
                  <li>✓ Get the exact words that will convince your skeptics</li>
                  <li>✓ Predict your success probability before spending millions</li>
                </ul>
              </div>
              <div className="bg-white/10 backdrop-blur p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">No Buzzwords. Just Answers:</h3>
                <ul className="space-y-2">
                  <li>✓ "Sarah needs proof it works before she'll try it"</li>
                  <li>✓ "Mike will adopt anything that saves him time"</li>
                  <li>✓ "Jennifer needs to protect her team first"</li>
                  <li>✓ "Tom just wants clear processes"</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* The Close */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              You Have Two Choices
            </h2>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-red-50 p-8 rounded-lg">
                <h3 className="text-xl font-bold text-red-900 mb-4">Keep Guessing</h3>
                <p className="text-gray-700">
                  Hope your team adapts. Hope your training works. Hope you're not part of the 70% that fails.
                </p>
                <p className="text-3xl font-bold text-red-600 mt-4">
                  Average loss: $12.5M
                </p>
              </div>
              <div className="bg-green-50 p-8 rounded-lg">
                <h3 className="text-xl font-bold text-green-900 mb-4">Know for Sure</h3>
                <p className="text-gray-700">
                  Measure what matters. Support each person correctly. Join the 30% that succeeds.
                </p>
                <p className="text-3xl font-bold text-green-600 mt-4">
                  Average ROI: 380%
                </p>
              </div>
            </div>
            
            <p className="text-xl text-gray-700 mb-8">
              We've helped 100+ companies beat the odds. Your competition is already using this.
            </p>
            
            <button 
              onClick={() => {
                trackCTAClick('hero_cta_assessment', 'ogilvy_home');
                trackOutboundLink('https://iam.neupath.ai');
                window.open('https://iam.neupath.ai', '_blank');
              }}
              className="bg-teal-600 text-white px-10 py-5 rounded-full text-xl font-bold hover:bg-teal-700 transition transform hover:-translate-y-0.5 inline-flex items-center mb-4"
            >
              Take the Assessment Now <ArrowRight className="ml-3" size={24} />
            </button>
            
            <p className="text-sm text-gray-600">
              5 minutes per person. No consultants. Just answers.
            </p>
          </div>
        </section>
      </>
    );
  };

  // Main HomePage - Uses HomePagePreLaunch with new interactive hero
  // TODO: Re-enable A/B testing when ready to launch
  const HomePage = () => {
    return <HomePagePreLaunch />;
  };

  // IAM Thesis Page Component - Variant A: Current Version
  const IAMThesisPageA = () => (
      <>
        {/* Hero Section */}
        <section className="pt-24 pb-20 bg-gradient-to-br from-teal-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-8">
                Why 74% of AI Transformations Fail
                <span className="text-teal-600 block">(And How to Be Different)</span>
              </h1>
              <div className="bg-blue-50 border-l-4 border-teal-500 p-6 rounded-r-lg max-w-4xl mx-auto">
                <div className="flex items-start">
                  <Lightbulb className="text-teal-500 mr-4 flex-shrink-0 mt-1" size={32} />
                  <div className="text-left">
                    <p className="text-xl font-semibold text-slate-800 mb-2">The overlooked factor in AI success</p>
                    <p className="text-lg text-gray-700">While everyone focuses on technology, <span className="font-semibold">71% of employees need support with AI adoption</span>. The real opportunity is human-centered transformation.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center max-w-4xl mx-auto">
              <p className="text-xl text-gray-700 mb-6">
                Traditional change management achieves only <span className="font-semibold text-amber-600">18-30% success rates</span>. 
                But organizations using human-centered approaches see <span className="font-semibold text-green-600">76-79% success</span>.
              </p>
              <p className="text-lg text-gray-600">
                The key insight? AI transformation is 10% technology and 90% people.
              </p>
            </div>
          </div>
        </section>

        {/* Key Discovery Section with 3D Sankey */}
        <section className="py-20 bg-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                What we've learned about AI adoption:
              </h2>
              <p className="text-2xl text-teal-400 font-semibold">
                There's no single "right" way to adapt to AI.
              </p>
            </div>
            
            <SankeyDiagram />
          </div>
        </section>

      {/* For Leaders Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              For Leaders Who Put People First
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your people are asking themselves: <em>"Will I still matter? Can I adapt fast enough?"</em>
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">The IAM Assessment gives you:</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="text-teal-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <span><strong>Clear sight</strong> of who needs what kind of support</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-teal-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <span><strong>Early warning</strong> when you're approaching critical failure thresholds</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-teal-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <span><strong>Multiple paths</strong> to success (because forcing everyone to be an "AI enthusiast" is impossible)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-teal-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <span><strong>Common language</strong> that respects differences rather than stigmatizing them</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-lg">
              <div className="flex items-start mb-4">
                <TrendingUp className="text-amber-600 mr-3 flex-shrink-0" size={24} />
                <h3 className="text-2xl font-bold text-slate-900">The Opportunity for Early Movers</h3>
              </div>
              <p className="text-gray-700 mb-4">
                Organizations that embrace human-centered AI adoption now see:
              </p>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <span className="text-3xl font-bold text-amber-600 mr-3">3x</span>
                  <span>higher success when managing conviction levels</span>
                </li>
                <li className="flex items-center">
                  <span className="text-3xl font-bold text-green-600 mr-3">25%</span>
                  <span>tipping point where change becomes self-sustaining</span>
                </li>
                <li className="flex items-center">
                  <span className="text-3xl font-bold text-blue-600 mr-3">60%</span>
                  <span>threshold where transformation accelerates</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="text-center p-8 bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg">
            <p className="text-xl font-semibold text-slate-900 mb-4">
              The choice is clear:
            </p>
            <p className="text-2xl font-bold text-teal-600">
              Focus on technology and join the 74% who fail, or focus on people and join the successful few.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Stop treating AI transformation like a technology project.
          </h2>
          <p className="text-2xl mb-8 font-semibold">
            Start treating it like the human journey it is.
          </p>
          <div className="bg-white/10 backdrop-blur rounded-lg p-6 mb-8 max-w-2xl mx-auto">
            <p className="text-lg italic mb-2">
              "Because the future of work isn't about humans versus AI. It's about humans with AI, each finding their unique way to thrive."
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://iam.neupath.ai"
              className="inline-flex items-center bg-white text-teal-700 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition transform hover:-translate-y-0.5"
              onClick={() => trackCTAClick('take_assessment', 'thesis_cta')}
            >
              Take the Assessment <ArrowRight className="ml-2" size={20} />
            </a>
            <button
              onClick={() => setCurrentPage('contact')}
              className="inline-flex items-center border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/10 transition"
            >
              Book a Demo
            </button>
          </div>
        </div>
      </section>
    </>
  );

  // IAM Thesis Page Component - Variant B: Ogilvy-Style Truth Telling
  const IAMThesisPageB = () => {
    const [preparationLevel, setPreparationLevel] = useState(30);
    
    return (
      <>
        {/* Hero - The Uncomfortable Truth */}
        <section className="pt-24 pb-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-8">
                I'm Going to Tell You Something
                <span className="block text-red-600">Your Consultants Won't</span>
              </h1>
            <div className="bg-slate-700/50 border-l-4 border-amber-400 p-6 rounded-r-lg max-w-4xl mx-auto backdrop-blur">
              <div className="flex items-start">
                <Zap className="text-amber-400 mr-4 flex-shrink-0 mt-1" size={32} />
                <div className="text-left">
                  <p className="text-xl font-semibold text-amber-200 mb-2">AI adoption is accelerating 10x faster than the internet</p>
                  <p className="text-lg text-gray-200">In 18 months, companies with prepared teams will dominate. Those without? <span className="font-semibold">They'll be fighting for survival.</span></p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center max-w-4xl mx-auto">
            <p className="text-xl text-gray-200 mb-6">
              This isn't about <span className="font-semibold text-amber-400">if</span> AI will transform your industry—it's about <span className="font-semibold text-amber-400">when</span> and <span className="font-semibold text-amber-400">how fast</span>.
            </p>
            <p className="text-lg text-gray-300">
              Smart leaders aren't asking "Should we adopt AI?" They're asking "How do we bring our entire team along?"
            </p>
          </div>
        </div>
      </section>

      {/* The Change Wave Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              The Change Wave: Where Will Your Team Land?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every technological revolution creates winners and casualties. The difference? <span className="font-semibold">Preparation.</span>
            </p>
          </div>
          
          <SankeyDiagram />
          
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
              <h3 className="font-bold text-lg mb-2 text-red-900">The Unprepared (74%)</h3>
              <p className="text-gray-700">Rush into AI tools without understanding their team's readiness. Result: Resistance, failed implementations, wasted investment.</p>
            </div>
            <div className="bg-amber-50 p-6 rounded-lg border-l-4 border-amber-500">
              <h3 className="font-bold text-lg mb-2 text-amber-900">The Reactive (20%)</h3>
              <p className="text-gray-700">Wait to see what happens, then scramble to catch up. Result: Always behind, fighting for talent, losing market share.</p>
            </div>
            <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
              <h3 className="font-bold text-lg mb-2 text-green-900">The Prepared (6%)</h3>
              <p className="text-gray-700">Understand their team's profiles, address concerns early, build champions. Result: Smooth adoption, competitive advantage, team unity.</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Preparation Advantage Section */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              The Preparation Advantage
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Companies that understand their team's AI readiness profiles see:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">With I AM Assessment:</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <strong>Identify hidden champions</strong>
                    <p className="text-gray-600 text-sm">Find your AI advocates before they become frustrated and leave</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <strong>Address real concerns</strong>
                    <p className="text-gray-600 text-sm">Understand what's really holding your team back (hint: it's not the technology)</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <strong>Create tailored pathways</strong>
                    <p className="text-gray-600 text-sm">Different people need different approaches - one size fails all</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <strong>Move 3x faster</strong>
                    <p className="text-gray-600 text-sm">When people feel heard and supported, adoption accelerates</p>
                  </div>
                </li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">The Clock Is Ticking:</h3>
              <div className="space-y-6">
                <div className="bg-white/80 p-4 rounded-lg">
                  <p className="text-3xl font-bold text-amber-600 mb-1">6 months</p>
                  <p className="text-gray-700">Until AI adoption becomes table stakes in most industries</p>
                </div>
                <div className="bg-white/80 p-4 rounded-lg">
                  <p className="text-3xl font-bold text-blue-600 mb-1">18 months</p>
                  <p className="text-gray-700">Until prepared companies have insurmountable advantages</p>
                </div>
                <div className="bg-white/80 p-4 rounded-lg">
                  <p className="text-3xl font-bold text-green-600 mb-1">Now</p>
                  <p className="text-gray-700 font-semibold">The optimal time to assess and prepare your team</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            The AI revolution won't wait for your team to be ready.
          </h2>
          <p className="text-2xl mb-8 text-amber-300 font-semibold">
            But you can get them ready now.
          </p>
          <div className="bg-white/10 backdrop-blur rounded-lg p-6 mb-8 max-w-2xl mx-auto">
            <p className="text-lg">
              Stop guessing. Start knowing. The I AM Assessment reveals exactly where your team stands and shows you the path forward.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://iam.neupath.ai"
              className="inline-flex items-center bg-amber-400 text-slate-900 px-8 py-4 rounded-full text-lg font-semibold hover:bg-amber-300 transition transform hover:-translate-y-0.5"
              onClick={() => trackCTAClick('take_assessment', 'thesis_variant_b')}
            >
              Assess Your Team Now <ArrowRight className="ml-2" size={20} />
            </a>
            <button
              onClick={() => setCurrentPage('contact')}
              className="inline-flex items-center border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/10 transition"
            >
              Talk to an Expert
            </button>
          </div>
        </div>
      </section>
      </>
    );
  };

  // Screenshot Carousel Component
  const ScreenshotCarousel = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    
    const slides = [
      {
        image: '/iam-dashboard.png',
        title: 'Real-time Organizational Insights',
        description: 'Monitor AI readiness across your entire organization with actionable metrics'
      },
      {
        image: '/iam-journey.png', 
        title: 'Track Individual Progress',
        description: 'Visualize where each team member is on their AI adoption journey'
      },
      {
        image: '/iam-assessment.png',
        title: 'Simple 5-Minute Assessment',
        description: 'Intuitive interface delivering valuable insights about your team'
      }
    ];

    // Auto-rotate carousel
    useEffect(() => {
      if (!isPaused) {
        const interval = setInterval(() => {
          setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 5000); // Change slide every 5 seconds
        
        return () => clearInterval(interval);
      }
    }, [isPaused, slides.length]);

    const goToSlide = (index: number) => {
      setCurrentSlide(index);
    };

    return (
      <div 
        className="relative max-w-5xl mx-auto"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Main carousel container */}
        <div className="relative overflow-hidden rounded-lg shadow-2xl bg-gray-100">
          <div 
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {slides.map((slide, index) => (
              <div key={index} className="w-full flex-shrink-0 relative">
                {/* Image container with aspect ratio - changed to show more of image */}
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img 
                    src={slide.image}
                    alt={slide.title}
                    className={`w-full h-full object-contain bg-gray-50 ${
                      index === currentSlide ? 'animate-slow-zoom' : ''
                    }`}
                  />
                  
                  {/* Overlay text with better visibility */}
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/90 via-black/70 to-transparent p-6">
                    <h3 className="text-white text-2xl font-bold mb-2 drop-shadow-lg">{slide.title}</h3>
                    <p className="text-white text-base drop-shadow-md">{slide.description}</p>
                  </div>
                  
                  {/* Subtle animation on active slide */}
                  {index === currentSlide && (
                    <div className="absolute inset-0 bg-gradient-to-t from-teal-600/5 to-transparent pointer-events-none" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation dots */}
        <div className="flex justify-center mt-6 space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-3 w-3 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-teal-600 w-8' 
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Optional: Side navigation arrows */}
        <button
          onClick={() => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition opacity-0 hover:opacity-100"
          aria-label="Previous slide"
        >
          <ChevronRight className="rotate-180" size={24} />
        </button>
        <button
          onClick={() => setCurrentSlide((prev) => (prev + 1) % slides.length)}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition opacity-0 hover:opacity-100"
          aria-label="Next slide"
        >
          <ChevronRight size={24} />
        </button>
      </div>
    );
  };

  // IAM Thesis Page Component - Variant C: Beyond the Hype
  const IAMThesisPageC = () => (
    <>
      {/* Hero Section */}
      <section className="pt-24 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-8">
              Beyond the AI Hype:
              <span className="text-teal-600 block mt-4">The Human Truth About Digital Transformation</span>
            </h1>
            <div className="bg-gradient-to-r from-blue-50 to-teal-50 border-l-4 border-teal-500 p-6 rounded-r-lg max-w-4xl mx-auto">
              <div className="flex items-start">
                <Brain className="text-teal-500 mr-4 flex-shrink-0 mt-1" size={32} />
                <div className="text-left">
                  <p className="text-xl font-semibold text-slate-800 mb-2">While everyone's chasing the latest AI tools...</p>
                  <p className="text-lg text-gray-700">True leaders are asking: <span className="font-semibold">"How do we prepare our people for what's coming?"</span></p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="p-4">
                <p className="text-4xl font-bold text-red-600 mb-2">$2.3T</p>
                <p className="text-sm text-gray-600">Wasted on digital transformation failures in 2023</p>
              </div>
              <div className="p-4">
                <p className="text-4xl font-bold text-amber-600 mb-2">87%</p>
                <p className="text-sm text-gray-600">Of employees fear AI will replace them</p>
              </div>
              <div className="p-4">
                <p className="text-4xl font-bold text-green-600 mb-2">3x</p>
                <p className="text-sm text-gray-600">Higher success with human-centered approach</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Reality Check Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Let's Cut Through the Noise
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              AI isn't magic. It's a tool. And like any tool, its success depends entirely on the people using it.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div>
              <h3 className="text-2xl font-bold text-red-400 mb-6">The Hype Says:</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <X className="text-red-400 mr-3 flex-shrink-0 mt-1" size={20} />
                  <span>"AI will solve all your problems"</span>
                </li>
                <li className="flex items-start">
                  <X className="text-red-400 mr-3 flex-shrink-0 mt-1" size={20} />
                  <span>"Just implement the technology and watch productivity soar"</span>
                </li>
                <li className="flex items-start">
                  <X className="text-red-400 mr-3 flex-shrink-0 mt-1" size={20} />
                  <span>"Your team will naturally adapt"</span>
                </li>
                <li className="flex items-start">
                  <X className="text-red-400 mr-3 flex-shrink-0 mt-1" size={20} />
                  <span>"Early adopters will pull everyone along"</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-green-400 mb-6">The Truth Is:</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-3 flex-shrink-0 mt-1" size={20} />
                  <span>AI amplifies existing strengths AND weaknesses</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-3 flex-shrink-0 mt-1" size={20} />
                  <span>Success requires deliberate change management</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-3 flex-shrink-0 mt-1" size={20} />
                  <span>Different people need different support</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-3 flex-shrink-0 mt-1" size={20} />
                  <span>Understanding your team's profiles is critical</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="bg-slate-800 p-8 rounded-lg text-center">
            <p className="text-2xl font-semibold text-teal-400 mb-4">
              "The companies winning with AI aren't the ones with the best technology.
            </p>
            <p className="text-xl text-gray-300">
              They're the ones who understood their people first."
            </p>
          </div>
        </div>
      </section>

      {/* The Smart Approach Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              The Smart, Systematic Approach
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stop gambling with AI adoption. Start with data about your most important asset: your people.
            </p>
          </div>
          
          <SankeyDiagram />
        </div>
      </section>

      {/* Intelligent Change Management Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Intelligent Change Management: The Future is Here
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The I AM Assessment is just the beginning. We're building the world's first <span className="font-bold text-purple-600">Intelligent Change Platform</span> - where AI amplifies human change leadership to deliver transformations at unprecedented speed and scale.
            </p>
          </div>

          {/* Evolution Comparison */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-red-500">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Traditional Change Management</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <X className="text-red-500 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <span>One-size-fits-all approaches</span>
                </li>
                <li className="flex items-start">
                  <X className="text-red-500 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <span>Quarterly pulse surveys</span>
                </li>
                <li className="flex items-start">
                  <X className="text-red-500 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <span>Generic communication plans</span>
                </li>
                <li className="flex items-start">
                  <X className="text-red-500 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <span className="font-bold">30% success rate</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-green-500">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Intelligent Change Management</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <span>Individual-level insights on every employee</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <span>Real-time adaptation and intervention</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <span>AI-generated personalized journeys</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <span className="font-bold">79% success rate</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Intelligent Change Agents */}
          <div className="bg-white rounded-lg p-10 shadow-xl mb-16">
            <h3 className="text-3xl font-bold text-slate-900 mb-8 text-center">
              Introducing Intelligent Change Agents
            </h3>
            <p className="text-xl text-gray-600 text-center mb-10">
              Imagine giving every change manager AI superpowers:
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-4xl mb-4">🧠</div>
                <h4 className="font-bold text-lg mb-2">See the Invisible</h4>
                <ul className="text-sm text-gray-600 space-y-1 text-left">
                  <li>• Understand each person's change DNA</li>
                  <li>• Predict resistance before it happens</li>
                  <li>• Identify hidden champions in your organization</li>
                </ul>
              </div>
              
              <div className="text-center">
                <div className="text-4xl mb-4">🎯</div>
                <h4 className="font-bold text-lg mb-2">Plan with Precision</h4>
                <ul className="text-sm text-gray-600 space-y-1 text-left">
                  <li>• Generate persona-specific strategies</li>
                  <li>• Create intervention plans that actually work</li>
                  <li>• Optimize resource allocation by impact</li>
                </ul>
              </div>
              
              <div className="text-center">
                <div className="text-4xl mb-4">⚡</div>
                <h4 className="font-bold text-lg mb-2">Execute at Scale</h4>
                <ul className="text-sm text-gray-600 space-y-1 text-left">
                  <li>• Automate personalized communications</li>
                  <li>• Trigger interventions at the right moment</li>
                  <li>• Scale one change manager to impact thousands</li>
                </ul>
              </div>
              
              <div className="text-center">
                <div className="text-4xl mb-4">📊</div>
                <h4 className="font-bold text-lg mb-2">Learn and Adapt</h4>
                <ul className="text-sm text-gray-600 space-y-1 text-left">
                  <li>• Track real behavior change, not just sentiment</li>
                  <li>• Continuously optimize strategies</li>
                  <li>• Prove ROI with hard data</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Platform Architecture */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-10 text-white mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">
              The Intelligent Change Platform Architecture
            </h3>
            <p className="text-xl text-center mb-10 opacity-90">
              Our platform transforms scattered efforts into orchestrated success:
            </p>
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-10">
              <div className="bg-white/20 backdrop-blur rounded-lg px-6 py-4 text-center">
                <h4 className="font-bold text-lg">Sense</h4>
              </div>
              <ArrowRight className="rotate-90 md:rotate-0" size={24} />
              <div className="bg-white/20 backdrop-blur rounded-lg px-6 py-4 text-center">
                <h4 className="font-bold text-lg">Plan</h4>
              </div>
              <ArrowRight className="rotate-90 md:rotate-0" size={24} />
              <div className="bg-white/20 backdrop-blur rounded-lg px-6 py-4 text-center">
                <h4 className="font-bold text-lg">Act</h4>
              </div>
              <ArrowRight className="rotate-90 md:rotate-0" size={24} />
              <div className="bg-white/20 backdrop-blur rounded-lg px-6 py-4 text-center">
                <h4 className="font-bold text-lg">Learn</h4>
              </div>
            </div>
            
            <div className="space-y-3 max-w-4xl mx-auto">
              <div className="flex items-start">
                <span className="font-bold mr-2">Sense:</span>
                <span>The I AM Assessment reveals individual mindsets and organizational readiness</span>
              </div>
              <div className="flex items-start">
                <span className="font-bold mr-2">Plan:</span>
                <span>AI generates targeted action plans for each persona and department</span>
              </div>
              <div className="flex items-start">
                <span className="font-bold mr-2">Act:</span>
                <span>Automated workflows execute personalized interventions at scale</span>
              </div>
              <div className="flex items-start">
                <span className="font-bold mr-2">Learn:</span>
                <span>Real-time dashboards track adoption and optimize strategies</span>
              </div>
            </div>
          </div>

          {/* Why This Matters Now */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-8 rounded-r-lg">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Why This Matters Now</h3>
            <p className="text-lg text-gray-700 mb-6">
              In the age of AI transformation, the human element has become the critical bottleneck. While technology advances exponentially, our ability to help humans adapt hasn't kept pace - until now.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg">
                <h4 className="font-bold text-amber-600 mb-2">The Multiplier Effect</h4>
                <p className="text-gray-600">One change professional + Intelligent Change Platform = The impact of an entire change team</p>
              </div>
              <div className="bg-white p-6 rounded-lg">
                <h4 className="font-bold text-amber-600 mb-2">The Speed Advantage</h4>
                <p className="text-gray-600">Reduce transformation timelines from years to months</p>
              </div>
              <div className="bg-white p-6 rounded-lg">
                <h4 className="font-bold text-amber-600 mb-2">The Success Guarantee</h4>
                <p className="text-gray-600">Move from hoping for adoption to engineering it</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <p className="text-xl text-gray-600 mb-6">
              Ready to transform how your team adopts AI?
            </p>
            <button
              onClick={() => setCurrentPage('contact')}
              className="inline-flex items-center bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-10 py-4 rounded-full text-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition transform hover:-translate-y-0.5 shadow-lg"
            >
              Talk to Us <ArrowRight className="ml-2" size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Continue with the existing content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mt-16 bg-gradient-to-br from-teal-50 to-blue-50 p-12 rounded-lg">
            <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">Your Roadmap to Real AI Success</h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              {[
                {
                  step: "1",
                  title: "Assess",
                  description: "Understand your team's real AI readiness with I AM Assessment"
                },
                {
                  step: "2",
                  title: "Map",
                  description: "Identify champions, skeptics, and everyone in between"
                },
                {
                  step: "3",
                  title: "Plan",
                  description: "Create personalized adoption paths for each profile"
                },
                {
                  step: "4",
                  title: "Execute",
                  description: "Roll out AI with confidence, not hope"
                },
                {
                  step: "5",
                  title: "Thrive",
                  description: "Watch your prepared team outperform the competition"
                }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2">{item.title}</h4>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white rounded-lg shadow-lg">
              <Target className="w-12 h-12 text-teal-600 mx-auto mb-4" />
              <h4 className="font-bold text-lg mb-2">Precision Over Panic</h4>
              <p className="text-gray-600">Know exactly who needs what support, when</p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-lg">
              <Users className="w-12 h-12 text-teal-600 mx-auto mb-4" />
              <h4 className="font-bold text-lg mb-2">Unity Over Division</h4>
              <p className="text-gray-600">Bring everyone along, not just the tech-savvy</p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-lg">
              <TrendingUp className="w-12 h-12 text-teal-600 mx-auto mb-4" />
              <h4 className="font-bold text-lg mb-2">Progress Over Perfection</h4>
              <p className="text-gray-600">Multiple paths to success, not one rigid approach</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Be the leader who sees beyond the hype.
          </h2>
          <p className="text-2xl mb-8 font-semibold">
            Your team's success with AI starts with understanding them.
          </p>
          <div className="bg-white/10 backdrop-blur rounded-lg p-6 mb-8 max-w-2xl mx-auto">
            <p className="text-lg">
              The I AM Assessment gives you the insights that million-dollar consultants miss: 
              How your actual people feel about AI, and what they need to succeed with it.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => {
                setCurrentPage('contact');
                trackCTAClick('early_access', 'thesis_variant_c');
              }}
              className="inline-flex items-center bg-white text-teal-700 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition transform hover:-translate-y-0.5"
            >
              Get Your Team's AI Readiness Score <ArrowRight className="ml-2" size={20} />
            </button>
          </div>
        </div>
      </section>
    </>
  );

  // Main IAM Thesis Page with variant selector (for testing)
  // IAM Thesis Page - Using Beyond the Hype version
  const IAMThesisPage = IAMThesisPageC;

  // About Page Component
  const AboutPage = () => (
    <div className="pt-24 pb-20">
      <section className="py-20 bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-8 text-center">
            AI Success Through Human Connection
          </h1>
          <p className="text-xl text-gray-600 text-center mb-12">
            We learned something important: Companies don't fail at AI because of bad technology. 
            They fail because they focus on the technology instead of their outcomes, and they forget about their people along the way.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Our Story</h2>
            <p className="text-lg text-gray-600 mb-4">
              Between us, we have over 60 years helping organizations navigate technology transformations 
              and nearly a decade specifically implementing AI solutions. Throughout all those projects, 
              we kept seeing the same pattern: brilliant technology implementations that failed 
              because nobody thought about the humans who had to use them.
            </p>
            <p className="text-lg text-gray-600 mb-4">
              When AI became mainstream, we watched this problem explode. Companies were so excited about 
              the technology that they forgot their people were terrified. 71% of employees 
              fear AI will replace them, but in most companies, they can't even talk about it.
            </p>
            <p className="text-lg text-gray-600 mb-4">
              Through our extensive experience and research, we discovered something fascinating: 
              there are 4 distinct ways people approach AI transformation, and each needs different support. 
              When you match the support to the person, success rates triple.
            </p>
            <p className="text-lg text-gray-600 font-semibold">
              NeuPath exists because we believe every company deserves to succeed with AI, 
              and every employee deserves to feel valued during the transition. Our decades of 
              experience have taught us that technology is easy—people are the real challenge and opportunity.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Our Approach</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="text-teal-600" size={40} />
                </div>
                <h3 className="text-xl font-semibold mb-2">Outcomes First</h3>
                <p className="text-gray-600">We start with what you want to achieve, not what technology can do</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="text-teal-600" size={40} />
                </div>
                <h3 className="text-xl font-semibold mb-2">People Always</h3>
                <p className="text-gray-600">Every strategy includes a plan for bringing your entire team along</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lightbulb className="text-teal-600" size={40} />
                </div>
                <h3 className="text-xl font-semibold mb-2">Practical Action</h3>
                <p className="text-gray-600">Real solutions that work, not one-size-fits-all approaches</p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Leadership Team</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                  <img 
                    src="/Alex-Headshot-NeuPath.jpeg" 
                    alt="Alexandra Wintrich" 
                    className="w-32 h-32 rounded-full object-cover flex-shrink-0"
                  />
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Alexandra Wintrich</h3>
                    <p className="text-teal-600 font-medium mb-3">Co-founder</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-3">
                  With over 25 years of experience in program management, process improvement, and change management, 
                  Alexandra brings deep expertise in guiding organizations through complex transformations. She recognizes 
                  the critical nature of a solid foundation and cohesive team for business and IT program success.
                </p>
                <p className="text-gray-600">
                  Alexandra's unique approach combines technical excellence with a deep understanding of the human elements 
                  that drive successful AI adoption. Her leadership has helped hundreds of organizations navigate the 
                  intersection of technology and human potential.
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                  <img 
                    src="/Mike-Headshot-NeuPath.jpeg" 
                    alt="Mike Alvarez" 
                    className="w-32 h-32 rounded-full object-cover flex-shrink-0"
                  />
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Mike Alvarez</h3>
                    <p className="text-teal-600 font-medium mb-3">Co-founder</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-3">
                  Over 15 years driving transformative change across finance, healthcare, and supply chain 
                  industries from Fortune 15 to SMBs. Mike has led dozens of commercial product teams and delivered hundreds 
                  of millions in value. His servant leadership philosophy focuses on enriching the lives of team members 
                  while achieving breakthrough results.
                </p>
                <p className="text-gray-600">
                  Starting his career at CompuServe was an amazing learning experience from a technology perspective, but what 
                  he remembers most is what being disrupted FELT like. That early lesson has hardwired into him to lead teams 
                  and companies through change, and this is why he created NeuPath.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Organizations Choose Us</h2>
            <ul className="space-y-4">
              {[
                "We speak your language, not tech jargon",
                "We understand real-world constraints and opportunities",
                "We measure success by your business results",
                "We ensure no one on your team gets left behind",
                "We provide ongoing support as you grow"
              ].map((item, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircle className="text-teal-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-lg text-gray-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );

  // Services Page Component
  const ServicesPage = () => (
    <div className="pt-24 pb-20">
      <section className="py-20 bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-8">
            Services
          </h1>
          <p className="text-xl text-gray-600">
            AI-powered tools and expert services designed to ensure your team thrives in the AI era.
          </p>
        </div>
      </section>

      {/* I AM Assessment Product Section */}
      <section id="iam-assessment" className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-teal-600 text-white rounded-full text-sm font-semibold mb-4">
              FLAGSHIP PRODUCT
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Individual Adoption Mindset (I AM) Assessment
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              The only AI readiness assessment that reveals how each person on your team really feels about AI—and shows you exactly how to support them.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3 className="text-2xl font-bold mb-6 text-teal-400">What Makes I AM Different</h3>
              <p className="text-gray-300 mb-6">
                Traditional assessments measure technical skills or organizational maturity. The I AM Assessment measures what actually determines success: 
                <span className="font-semibold text-white"> your people's mindsets, concerns, and readiness for change.</span>
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <Brain className="text-teal-400 mr-3 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold mb-1">Behavioral Science Foundation</h4>
                    <p className="text-gray-400 text-sm">Based on proven change management psychology, not generic tech surveys</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Users className="text-teal-400 mr-3 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold mb-1">Individual & Team Insights</h4>
                    <p className="text-gray-400 text-sm">Understand each person's unique profile plus overall team dynamics</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Target className="text-teal-400 mr-3 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold mb-1">Actionable Strategies</h4>
                    <p className="text-gray-400 text-sm">Specific recommendations for each persona type, not generic advice</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 p-8 rounded-lg">
              <h3 className="text-xl font-bold mb-6">The 4 AI Adoption Profiles</h3>
              <p className="text-gray-300 mb-6">Every person falls into one of these profiles:</p>
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="font-semibold">Pathfinder</span> - Pioneering innovators who lead the way
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                  <span className="font-semibold">Process Architect</span> - Systems thinkers who build frameworks
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                  <span className="font-semibold">Team Guardian</span> - People-focused leaders who protect culture
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                  <span className="font-semibold">Proof Seeker</span> - Evidence-driven decision makers
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-teal-600 to-blue-600 p-8 rounded-lg mb-12">
            <h3 className="text-2xl font-bold mb-6">How It Works</h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-xl">1</span>
                </div>
                <h4 className="font-semibold mb-2">5-Minute Survey</h4>
                <p className="text-sm opacity-90">Team members complete a confidential behavioral assessment</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-xl">2</span>
                </div>
                <h4 className="font-semibold mb-2">AI Analysis</h4>
                <p className="text-sm opacity-90">Our AI identifies patterns and creates persona profiles</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-xl">3</span>
                </div>
                <h4 className="font-semibold mb-2">Intelligent Insights</h4>
                <p className="text-sm opacity-90">Leaders see team composition and readiness metrics</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-xl">4</span>
                </div>
                <h4 className="font-semibold mb-2">Action Plans</h4>
                <p className="text-sm opacity-90">Receive tailored strategies for each persona group</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-800 p-6 rounded-lg">
              <h3 className="text-xl font-bold mb-4 text-teal-400">For Leaders</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Identify hidden champions and potential blockers</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Understand team's actual (not assumed) concerns</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Get specific communication strategies per persona</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Track readiness progress over time</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-slate-800 p-6 rounded-lg">
              <h3 className="text-xl font-bold mb-4 text-teal-400">For Team Members</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Feel heard and understood, not judged</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Receive personalized support and resources</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Connect with others on similar journey</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Build confidence at their own pace</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Screenshots Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Intelligent Insights in Action</h2>
            <p className="text-xl text-gray-600">Experience the power of AI-driven organizational analysis</p>
          </div>

          {/* AI Coach Insights - Hover Effect */}
          <div className="max-w-5xl mx-auto mb-16">
            <div className="relative group cursor-pointer">
              <div className="relative overflow-hidden rounded-lg shadow-xl bg-white">
                <img 
                  src="/GenerativeAI-GuidedOverview-QA.png"
                  alt="AI Change Coach Interface"
                  className="w-full h-auto transition-all duration-500 ease-in-out group-hover:opacity-10"
                />
                
                {/* Hover Overlay with Strategic Recommendations */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500 ease-in-out pointer-events-none">
                  <div className="p-8 flex items-center justify-center h-full">
                    <div className="bg-white rounded-lg p-4 max-w-4xl w-full" style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}>
                      <img 
                        src="/Strategic-AI-Recommendations.png"
                        alt="Strategic AI Recommendations"
                        className="w-full h-auto rounded"
                      />
                      <div className="mt-4 p-6 bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg shadow-lg">
                        <p className="text-white font-semibold">
                          <span className="text-lg font-bold block mb-2">AI-Powered Recommendations:</span> 
                          Our AI Change Coach analyzes your assessment data to provide strategic recommendations for pilot projects, 
                          identifying the most ready teams with the optimal mix of change profiles.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="text-center mt-4 text-gray-600 italic">
                Hover to see AI-generated strategic recommendations
              </p>
            </div>
          </div>

          {/* Department Profile Analysis with Callout */}
          <div className="max-w-7xl mx-auto">
            <div className="relative group cursor-pointer">
              <div className="flex items-center justify-center">
                {/* Screenshot - starts large, shrinks on hover */}
                <div className="relative transition-all duration-700 ease-in-out group-hover:scale-75 group-hover:opacity-90">
                  <img 
                    src="/IAMDeptProfileAnalysis.png"
                    alt="Department Profile Analysis"
                    className="w-full max-w-5xl h-auto rounded-lg shadow-xl"
                  />
                </div>
                
                {/* Explanation box - starts small/hidden, grows and overlaps on hover */}
                <div className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-8 opacity-0 scale-90 
                              group-hover:opacity-100 group-hover:scale-100 group-hover:translate-x-0
                              transition-all duration-700 ease-in-out pointer-events-none">
                  <div className="bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg shadow-2xl max-w-xl" 
                       style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)' }}>
                    <h3 className="text-2xl font-bold text-slate-900 mb-4">Drill Down by Department or Profile</h3>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <Target className="text-teal-600 mr-3 flex-shrink-0 mt-1" size={24} />
                        <div>
                          <h4 className="font-semibold text-slate-800 mb-1">Identify Ready Teams</h4>
                          <p className="text-gray-600 text-sm">Pinpoint departments with the highest readiness scores and optimal profile mix for AI pilot projects.</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <Users className="text-blue-600 mr-3 flex-shrink-0 mt-1" size={24} />
                        <div>
                          <h4 className="font-semibold text-slate-800 mb-1">Balance Team Composition</h4>
                          <p className="text-gray-600 text-sm">Ensure the right mix of Pathfinders, Process Architects, Team Guardians, and Proof Seekers for successful execution.</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <AlertTriangle className="text-amber-600 mr-3 flex-shrink-0 mt-1" size={24} />
                        <div>
                          <h4 className="font-semibold text-slate-800 mb-1">Address Department Concerns</h4>
                          <p className="text-gray-600 text-sm">Receive targeted warnings like: <em>"Operations needs awareness building and change readiness training before AI implementation."</em></p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-6 p-4 bg-white rounded-lg border-l-4 border-teal-600">
                      <p className="text-sm text-gray-700">
                        <strong>Your AI Change Coach</strong> provides real-time guidance, answering questions about your assessment 
                        and offering personalized recommendations for each department's unique needs.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="text-center mt-4 text-gray-600 italic">
                Hover to explore department-level insights and recommendations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - ALIGN, ACTIVATE, EVOLVE */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">NeuPath Services</h2>
            <p className="text-xl text-gray-600">A proven three-phase approach to AI transformation</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* ALIGN Phase */}
            <div className="bg-gradient-to-br from-teal-50 to-white rounded-lg p-8 border border-teal-100">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white bg-teal-600 rounded-full py-3 px-6 inline-block mb-4">ALIGN</h3>
                <div className="space-y-2 text-gray-700 font-medium">
                  <p>Define Outcomes</p>
                  <p>Assess Readiness</p>
                  <p>Empower People</p>
                </div>
              </div>
              <div className="border-t pt-6">
                <h4 className="font-semibold text-slate-900 mb-4">Services:</h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="text-teal-600 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">Strategic AI Planning</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-teal-600 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">I AM Assessment</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-teal-600 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">Persona-Based Enablement</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* ACTIVATE Phase */}
            <div className="bg-gradient-to-br from-teal-50 to-white rounded-lg p-8 border border-teal-100">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white bg-teal-600 rounded-full py-3 px-6 inline-block mb-4">ACTIVATE</h3>
                <div className="space-y-2 text-gray-700 font-medium">
                  <p>Identify Use Cases</p>
                  <p>Build Business Case</p>
                  <p>Implement</p>
                </div>
              </div>
              <div className="border-t pt-6">
                <h4 className="font-semibold text-slate-900 mb-4">Services:</h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="text-teal-600 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">AI Opportunity Assessment</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-teal-600 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">ROI Modeling & Roadmap</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-teal-600 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">AI Implementation Support</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* EVOLVE Phase */}
            <div className="bg-gradient-to-br from-teal-50 to-white rounded-lg p-8 border border-teal-100">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white bg-teal-600 rounded-full py-3 px-6 inline-block mb-4">EVOLVE</h3>
                <div className="space-y-2 text-gray-700 font-medium">
                  <p>Measure</p>
                  <p>Learn</p>
                  <p>Scale</p>
                </div>
              </div>
              <div className="border-t pt-6">
                <h4 className="font-semibold text-slate-900 mb-4">Services:</h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="text-teal-600 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">Outcome Measurement</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-teal-600 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">Continuous Support</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-teal-600 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">Strategic Staffing</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Contact Section */}
          <div className="mt-16 bg-gradient-to-br from-teal-600 to-teal-700 rounded-lg p-12 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">Ready to Transform Your Organization?</h3>
            <p className="text-lg text-teal-50 mb-8 max-w-2xl mx-auto">
              Let's discuss how our proven approach can help you navigate your AI transformation journey with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setCurrentPage('contact')}
                className="px-8 py-3 bg-white text-teal-700 font-semibold rounded-lg hover:bg-teal-50 transition shadow-lg"
              >
                Schedule a Consultation
              </button>
              <a 
                href="mailto:hello@neupath.ai"
                className="px-8 py-3 bg-teal-800 text-white font-semibold rounded-lg hover:bg-teal-900 transition flex items-center justify-center gap-2"
              >
                <Mail size={20} />
                hello@neupath.ai
              </a>
            </div>
            <p className="text-sm text-teal-100 mt-6">
              Or call us directly to learn more about our services
            </p>
          </div>
        </div>
      </section>
    </div>
  );


  // Contact Page Component
  const ContactPage = () => {
    // Get URL parameters for prefilled interest from hash - only on initial mount
    const [interestParam] = useState(() => {
      const hash = window.location.hash;
      const queryIndex = hash.indexOf('?');
      const queryString = queryIndex > -1 ? hash.substring(queryIndex + 1) : '';
      const urlParams = new URLSearchParams(queryString);
      return urlParams.get('interest') || '';
    });
    
    return (
      <div className="pt-24 pb-20">
        <section className="py-20 bg-gradient-to-br from-gray-50 to-teal-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-8">
              {interestParam === 'Executive Briefing' 
                ? "Request Your Executive Briefing"
                : "Let's Start Your AI Journey Together"}
            </h1>
            <p className="text-xl text-gray-600">
              {interestParam === 'Executive Briefing'
                ? "Transform your organization with white-glove AI transformation services."
                : "Ready to achieve real outcomes through human-centered AI adoption? We're here to help."}
            </p>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ContactForm formSource="contact" prefilledInterest={interestParam} />
          </div>
        </section>
      </div>
    );
  };


  // Demo Page Component
  const DemoPage = () => (
    <div className="pt-24 pb-20">
      <section className="py-20 bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-8">
            See NeuPath in Action
          </h1>
          <p className="text-xl text-gray-600">
            Schedule a personalized demo to see how we can accelerate your AI transformation journey.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ContactForm formSource="demo" prefilledInterest="Schedule Demo" />
        </div>
      </section>
    </div>
  );

  // Page Renderer
  const renderPage = () => {
    switch(currentPage) {
      case 'about':
        return <AboutPage />;
      case 'services':
        return <ServicesPage />;
      case 'pricing':
        return <PricingROICalculator />;
      case 'contact':
        return <ContactPage />;
      case 'iam-thesis':
        return <IAMThesisPage />;
      case 'privacy':
        return <PrivacyPolicy />;
      case 'terms':
        return <TermsOfService />;
      case 'apply':
        return <AssessmentApplication />;
      case 'resources': {
        // Simple state-driven rendering - no URL parsing needed
        if (currentArticleId) {
          return (
            <ArticleDetailV2
              articleId={currentArticleId}
              onNavigate={setCurrentPage}
              onNavigateToArticle={navigateToArticle}
              onNavigateToResourcesList={navigateToResourcesList}
            />
          );
        }
        return (
          <ResourcesV2
            onNavigate={(page) => page === 'pricing' ? navigateToROICalculator() : setCurrentPage(page)}
            onNavigateToArticle={navigateToArticle}
          />
        );
      }
      case 'demo':
        return <DemoPage />;
      default:
        return <HomePage />;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      
      <Navigation />
      {renderPage()}
      
      {/* Cookie Banner */}
      <CookieBanner />
      
      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <div className="flex items-center mb-2">
                <svg className="w-8 h-8 mr-2" viewBox="0 0 100 100">
                  <path d="M20 50 Q 35 20, 50 50 T 80 50" stroke="#00A0B0" strokeWidth="4" fill="none"/>
                  <circle cx="20" cy="50" r="5" fill="#00A0B0"/>
                  <circle cx="50" cy="50" r="5" fill="#00A0B0"/>
                  <circle cx="80" cy="50" r="5" fill="#00A0B0"/>
                </svg>
                <span className="text-lg font-bold">NeuPath</span>
              </div>
              <p className="text-sm opacity-75 mb-2">Outcomes First. People Always.</p>
              <a href="mailto:hello@neupath.ai" className="text-sm text-teal-400 hover:text-teal-300 transition">
                hello@neupath.ai
              </a>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
              <div className="flex gap-4 text-sm opacity-75">
                <button onClick={() => setCurrentPage('privacy')} className="hover:opacity-100 transition">
                  Privacy Policy
                </button>
                <span>•</span>
                <button onClick={() => setCurrentPage('terms')} className="hover:opacity-100 transition">
                  Terms of Service
                </button>
              </div>
              <div className="text-sm opacity-75">
                © 2024 NeuPath. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;